<template>
  <div
    v-if="profile"
    class="card-perspective-wrapper"
    @mousemove="handleMouseMove"
    @mouseleave="handleMouseLeave"
  >
    <div
      ref="posterCard"
      class="member-poster-card font-web"
      :style="cardCustomTransform"
    >
      <!-- Top-Left & Bottom-Right Corner Glow Flares (Rare Card Accent) -->
      <div class="rare-corner-flare flare-top-left"></div>
      <div class="rare-corner-flare flare-bottom-right"></div>

      <!-- Holographic Foil & Sparkle Shimmer (Rare Card Effect) -->
      <div class="rare-holo-foil"></div>
      <div class="rare-sparkle-layer"></div>

      <!-- Photo Area -->
      <div class="member-photo-wrapper">
        <img
          v-if="photoUrl"
          :src="photoUrl"
          class="member-photo-img"
          alt="Staff Member"
        />
        <div v-else class="member-photo-placeholder">
          <a-icon type="user" class="placeholder-icon" />
        </div>
      </div>

      <!-- SVG Decorative Ribbon & Bottom Banner Shapes -->
      <svg
        class="member-card-decorations"
        viewBox="0 0 380 490"
        preserveAspectRatio="none"
      >
        <!-- Dark Gold Bevel Ribbon -->
        <polygon points="0,342 380,388 380,404 0,358" fill="#b99023" />
        <!-- Purple Bottom Banner -->
        <polygon points="0,356 380,402 380,490 0,490" fill="#41085a" />
        <!-- Bright Yellow Left Bar and Upper Ribbon -->
        <path
          d="M 0,68 L 52,68 L 52,324 L 380,368 L 380,390 L 0,344 Z"
          fill="#ffc624"
        />
      </svg>

      <!-- Left Role Label -->
      <div class="left-vertical-role-container">
        <span class="vertical-role-text">{{ roleText }}</span>
      </div>

      <!-- Bottom Content Information -->
      <div
        class="member-bottom-content font-web"
        :class="{ 'content-compact': isContentLong, 'content-ultra-compact': isContentVeryLong }"
      >
        <div
          class="member-fullname"
          :class="{ 'text-compact': isNameLong, 'text-ultra-compact': isNameVeryLong }"
        >
          {{ fullNameEn }}
        </div>
        <div class="member-meta-item">
          <span class="label">Position :</span> {{ positionText }}
        </div>
        <div class="member-meta-item">
          <span class="label">Division :</span> {{ divisionText }}
        </div>
        <div class="member-employment-date">
          <div>EMPLOYMENT STARTING DATE :</div>
          <div>{{ employmentDateText }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";

export default {
  name: "CardMember",
  props: {
    dataMember: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isHovered: false,
      tiltX: 0,
      tiltY: 0,
    };
  },
  computed: {
    profile() {
      return this.dataMember && this.dataMember.length > 0
        ? this.dataMember[0]
        : null;
    },
    cardCustomTransform() {
      if (this.isHovered) {
        return {
          animation: "none",
          transform: `rotateX(${this.tiltX}deg) rotateY(${this.tiltY}deg) scale3d(1.02, 1.02, 1.02)`,
        };
      }
      return {};
    },
    photoUrl() {
      return this.profile && this.profile.img ? this.profile.img : "";
    },
    fullNameEn() {
      if (!this.profile) return "-";
      const title =
        this.profile.title && this.profile.title.name_eng
          ? this.profile.title.name_eng.trim()
          : "";
      const name = this.profile.name ? this.profile.name.trim() : "";
      const surname = this.profile.surname ? this.profile.surname.trim() : "";
      const full = [title, name, surname].filter(Boolean).join(" ");
      return full ? full.toUpperCase() : "-";
    },
    positionText() {
      if (!this.profile) return "-";
      return (
        this.profile.position ||
        (this.profile.user_type && this.profile.user_type.name_eng) ||
        "-"
      );
    },
    roleText() {
      const pos = this.positionText;
      return pos && pos !== "-" ? pos.toUpperCase() : "MEMBER";
    },
    divisionText() {
      if (!this.profile) return "-";
      if (this.profile.department && this.profile.department.name_eng) {
        return this.profile.department.name_eng;
      }
      if (this.profile.division) {
        return this.profile.division;
      }
      return this.profile.top_department || "-";
    },
    employmentDateText() {
      if (!this.profile || !this.profile.worked) return "-";
      try {
        const m = this.moment || moment;
        return m(this.profile.worked)
          .locale("en")
          .format("Do MMMM YYYY")
          .toUpperCase();
      } catch (e) {
        return "-";
      }
    },
    isNameLong() {
      return this.fullNameEn.length > 25 && this.fullNameEn.length <= 36;
    },
    isNameVeryLong() {
      return this.fullNameEn.length > 36;
    },
    totalInfoLength() {
      return (
        (this.fullNameEn ? this.fullNameEn.length : 0) +
        (this.positionText ? this.positionText.length : 0) +
        (this.divisionText ? this.divisionText.length : 0)
      );
    },
    isContentLong() {
      return (
        this.totalInfoLength > 75 ||
        (this.positionText && this.positionText.length > 28) ||
        (this.divisionText && this.divisionText.length > 30)
      );
    },
    isContentVeryLong() {
      return (
        this.totalInfoLength > 105 ||
        (this.positionText && this.positionText.length > 40) ||
        (this.divisionText && this.divisionText.length > 45)
      );
    },
  },
  methods: {
    handleMouseMove(e) {
      const card = this.$refs.posterCard;
      if (!card) return;
      const rect = card.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;

      this.isHovered = true;
      this.tiltX = (((y - centerY) / centerY) * -10).toFixed(2);
      this.tiltY = (((x - centerX) / centerX) * 10).toFixed(2);
    },
    handleMouseLeave() {
      this.isHovered = false;
      this.tiltX = 0;
      this.tiltY = 0;
    },
  },
};
</script>

<style lang="scss" scoped>
@import url("https://fonts.googleapis.com/css2?family=Montserrat:wght@700;800;900&family=Open+Sans:wght@500;600;700;800&family=Sarabun:wght@400;500;600;700&display=swap");

.card-perspective-wrapper {
  perspective: 1200px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding-top: 10px;
  padding-bottom: 0;
}

.member-poster-card {
  width: 100%;
  max-width: 380px;
  height: 490px;
  margin: 0 auto;
  border-radius: 28px;
  background: #ffffff;
  position: relative;
  overflow: hidden;
  user-select: none;
  transform-style: preserve-3d;
  will-change: transform, box-shadow;
  transition: transform 0.2s cubic-bezier(0.25, 1, 0.5, 1), box-shadow 0.3s ease;
  animation: rareCardFlip 6s ease-in-out infinite alternate;
  box-shadow: 
    0 18px 45px rgba(0, 0, 0, 0.3),
    0 0 20px rgba(255, 198, 36, 0.3),
    0 0 40px rgba(185, 43, 226, 0.22);
}

/* 3D Diagonal Flip / Tilt Animation (Top-Left & Bottom-Right) */
@keyframes rareCardFlip {
  0% {
    transform: rotateX(6deg) rotateY(-8deg) translateY(-3px);
    box-shadow: 
      -10px 20px 45px rgba(0, 0, 0, 0.35),
      -6px -6px 25px rgba(255, 215, 0, 0.45),
      8px 8px 35px rgba(255, 75, 190, 0.25);
  }
  50% {
    transform: rotateX(-6deg) rotateY(8deg) translateY(3px);
    box-shadow: 
      10px 20px 45px rgba(0, 0, 0, 0.35),
      8px 8px 30px rgba(255, 215, 0, 0.45),
      -8px -8px 35px rgba(0, 210, 255, 0.3);
  }
  100% {
    transform: rotateX(6deg) rotateY(-8deg) translateY(-3px);
    box-shadow: 
      -10px 20px 45px rgba(0, 0, 0, 0.35),
      -6px -6px 25px rgba(255, 215, 0, 0.45),
      8px 8px 35px rgba(255, 75, 190, 0.25);
  }
}

/* Corner Glow Flares (Top-Left & Bottom-Right) */
.rare-corner-flare {
  position: absolute;
  width: 150px;
  height: 150px;
  border-radius: 50%;
  pointer-events: none;
  z-index: 15;
  mix-blend-mode: screen;
  filter: blur(22px);
  opacity: 0.65;
  animation: cornerFlareGlow 4s ease-in-out infinite alternate;
}

.flare-top-left {
  top: -45px;
  left: -45px;
  background: radial-gradient(
    circle,
    rgba(255, 230, 100, 0.8) 0%,
    rgba(255, 160, 20, 0.5) 45%,
    transparent 70%
  );
}

.flare-bottom-right {
  bottom: -45px;
  right: -45px;
  background: radial-gradient(
    circle,
    rgba(255, 105, 180, 0.75) 0%,
    rgba(138, 43, 226, 0.5) 50%,
    transparent 70%
  );
  animation-delay: -2s;
}

@keyframes cornerFlareGlow {
  0% {
    opacity: 0.35;
    transform: scale(0.85);
  }
  100% {
    opacity: 0.85;
    transform: scale(1.15);
  }
}

/* Holographic Shimmer Foil Layer */
.rare-holo-foil {
  position: absolute;
  top: -60%;
  left: -60%;
  width: 220%;
  height: 220%;
  pointer-events: none;
  z-index: 12;
  background: linear-gradient(
    125deg,
    transparent 28%,
    rgba(255, 255, 255, 0) 38%,
    rgba(255, 220, 100, 0.25) 42%,
    rgba(255, 60, 160, 0.22) 48%,
    rgba(60, 200, 255, 0.28) 53%,
    rgba(255, 255, 255, 0.45) 56%,
    rgba(255, 230, 120, 0.25) 59%,
    transparent 68%
  );
  mix-blend-mode: color-dodge;
  animation: rareHoloSweep 5s linear infinite;
}

@keyframes rareHoloSweep {
  0% {
    transform: translate(-30%, -30%) rotate(0deg);
  }
  100% {
    transform: translate(30%, 30%) rotate(0deg);
  }
}

/* Sparkles Accent */
.rare-sparkle-layer {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 14;
  background-image: 
    radial-gradient(2px 2px at 20px 40px, #ffffff, transparent),
    radial-gradient(2px 2px at 320px 80px, #ffd700, transparent),
    radial-gradient(3px 3px at 50px 240px, #ffffff, transparent),
    radial-gradient(2.5px 2.5px at 340px 320px, #ff80df, transparent),
    radial-gradient(2px 2px at 280px 420px, #ffe680, transparent);
  opacity: 0.55;
  animation: sparkleTwinkle 3s ease-in-out infinite alternate;
}

@keyframes sparkleTwinkle {
  0% {
    opacity: 0.3;
    transform: scale(0.98);
  }
  100% {
    opacity: 0.85;
    transform: scale(1.02);
  }
}

/* Photo Area */
.member-photo-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 380px;
  z-index: 1;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background: radial-gradient(circle at 55% 42%, #ffffff 0%, #d8dde3 85%);
  overflow: hidden;
}

.member-photo-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 15%;
}

.member-photo-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f2f5;

  .placeholder-icon {
    font-size: 90px;
    color: #b0b7c3;
  }
}

/* SVG Geometric Decor */
.member-card-decorations {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 3;
  pointer-events: none;
}

/* Left Vertical Role Container */
.left-vertical-role-container {
  position: absolute;
  top: 68px;
  left: 0;
  width: 52px;
  height: 264px;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.vertical-role-text {
  transform: rotate(-90deg);
  white-space: nowrap;
  font-size: 10px;
  font-weight: 700;
  color: #41085a;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  font-family: "Montserrat", sans-serif;
}

/* Bottom Info Section */
.member-bottom-content {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  max-height: 140px;
  box-sizing: border-box;
  overflow: hidden;
  z-index: 6;
  padding: 0 18px 14px 22px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  transition: all 0.2s ease;
}

.member-fullname {
  font-size: 15px;
  font-weight: 900;
  color: #ffffff;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 3px;
  line-height: 1.2;
  word-break: break-word;
  font-family: "Montserrat", "Sarabun", sans-serif;
  transition: font-size 0.2s ease;

  &.text-compact {
    font-size: 13px;
    line-height: 1.15;
    margin-bottom: 2px;
  }

  &.text-ultra-compact {
    font-size: 11.5px;
    line-height: 1.1;
    margin-bottom: 2px;
  }
}

.member-meta-item {
  font-size: 12.5px;
  color: #ffffff;
  font-weight: 500;
  line-height: 1.35;
  margin-bottom: 2px;
  word-break: break-word;
  font-family: "Open Sans", "Sarabun", sans-serif;
  transition: font-size 0.2s ease, line-height 0.2s ease;

  .label {
    font-weight: 600;
  }
}

.member-employment-date {
  margin-top: 3px;
  font-size: 10.5px;
  font-weight: 800;
  color: #ffc624;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  line-height: 1.3;
  font-family: "Montserrat", "Sarabun", sans-serif;
  transition: font-size 0.2s ease;
}

/* Compact scale when overall text length is long */
.member-bottom-content.content-compact {
  padding: 0 16px 12px 20px;

  .member-fullname:not(.text-compact):not(.text-ultra-compact) {
    font-size: 13.5px;
    margin-bottom: 2px;
  }

  .member-meta-item {
    font-size: 11.5px;
    line-height: 1.25;
    margin-bottom: 1.5px;
  }

  .member-employment-date {
    margin-top: 2px;
    font-size: 9.5px;
    line-height: 1.25;
  }
}

/* Ultra-compact scale when text is exceptionally long */
.member-bottom-content.content-ultra-compact {
  padding: 0 14px 10px 18px;

  .member-fullname {
    font-size: 11.5px;
    line-height: 1.1;
    margin-bottom: 1.5px;
  }

  .member-meta-item {
    font-size: 10px;
    line-height: 1.2;
    margin-bottom: 1px;
  }

  .member-employment-date {
    margin-top: 2px;
    font-size: 8.5px;
    line-height: 1.2;
  }
}
</style>
